import React, { KeyboardEvent, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { ReactComponent as SendIcon } from '../../../shared/icons/send-icon.svg';
import { ReactComponent as MicIcon } from '../../../shared/icons/mic-icon.svg';
import { ReactComponent as StopIcon } from '../../../shared/icons/stop-icon.svg';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useIsMobile } from '../../../shared/hooks/useIsMobile';
import classNames from 'classnames';
import styles from './ChatInput.module.scss';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
}

export interface ChatInputRef {
  focus: () => void;
}

export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
}, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  // Expose focus method via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    }
  }), []);
  const {
    isListening,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    resetTranscript,
    isSupported: isSpeechSupported,
    error: speechError,
  } = useSpeechRecognition({
    continuous: true,
    interimResults: true,
    maxSilenceMs: 2000,
  });

  // Update input value with transcript
  useEffect(() => {
    if (transcript) {
      onChange(transcript);
    }
  }, [transcript, onChange]);

  // Auto-submit when recording stops and we have text
  useEffect(() => {
    if (!isListening && transcript && transcript.trim()) {
      onSend();
      resetTranscript();
    }
  }, [isListening, transcript, onSend, resetTranscript]);

  const handleMicClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      // Clear existing text when starting new recording
      onChange('');
      resetTranscript();
      startListening();
    }
  }, [isListening, startListening, stopListening, onChange, resetTranscript]);
  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) {
        onSend();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  // Auto-resize textarea
  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 120)}px`;
  };

  // Show interim transcript while recording
  const displayValue = isListening && interimTranscript ? interimTranscript : value;

  return (
    <div className={styles.inputContainer}>
      <div className={styles.inputWrapper}>
        <textarea
          ref={textareaRef}
          className={styles.input}
          value={displayValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onInput={(e) => adjustTextareaHeight(e.currentTarget)}
          placeholder={isListening ? 'Listening...' : placeholder}
          disabled={disabled || isListening}
          rows={1}
        />
        
        <button
          type="button"
          className={styles.sendButton}
          onClick={onSend}
          disabled={disabled || !value.trim()}
          aria-label="Send message"
        >
          <SendIcon />
        </button>

        {/* Show mic button only on mobile and if speech is supported */}
        {isMobile && isSpeechSupported && (
          <button
            type="button"
            className={classNames(styles.micButton, {
              [styles.recording]: isListening,
            })}
            onClick={handleMicClick}
            disabled={disabled}
            title={isListening ? 'Stop recording' : 'Start voice input'}
            aria-label={isListening ? 'Stop recording' : 'Start voice input'}
          >
            {isListening ? <StopIcon /> : <MicIcon />}
          </button>
        )}
      </div>
      
      <div className={styles.hint}>
        {isListening ? (
          <span className={styles.recordingHint}>Speak your message... (auto-stops after 2s silence)</span>
        ) : speechError ? (
          <span className={styles.errorHint}>{speechError}</span>
        ) : (
          'Press Enter to send, Shift+Enter for new line'
        )}
      </div>
    </div>
  );
});